import { useEffect, useRef } from 'react';
import { Divider, App as AntApp } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useGoogleLoginMutation, isContextRequired } from './hooks';
import type { ContextPickerState } from './ContextPickerPage';
import { ApiError } from '@starling/core/api/errors';

// «Войти через Google» (GIS id_token, M7.5) — блок под формой /login:
// разделитель «или» + официальная кнопка Google (рендерится самим GIS,
// не стилизуем — наша забота только контейнер/отступы).
//
// Поведение:
//   * `VITE_GOOGLE_CLIENT_ID` не задан → компонент не рендерит НИЧЕГО
//     (ни заглушки, ни ошибки) — симметрично бэку, где без GOOGLE_ENABLED
//     эндпоинт отвечает 404.
//   * GIS-скрипт грузится ЛЕНИВО — только здесь и только при заданном
//     client_id (динамический <script>, НЕ глобально в index.html).
//   * One Tap / auto prompt НЕ используем — только явная кнопка.
//   * callback GIS отдаёт credential (id_token) → POST /auth/google →
//     обработка РОВНО как у login: TokenPair → setTokens в мутации
//     (редирект по роли сделает эффект LoginPage на isAuthenticated);
//     ContextRequired → навигация на экран «Войти как…».

const GSI_SRC = 'https://accounts.google.com/gsi/client';

export function GoogleSignInButton(): JSX.Element | null {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '';
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { message } = AntApp.useApp();
  const mutation = useGoogleLoginMutation();
  const containerRef = useRef<HTMLDivElement | null>(null);

  // GIS-callback живёт вне React-жизненного цикла (регистрируется один раз
  // в initialize) → зовём актуальный обработчик через ref, чтобы не поймать
  // устаревшие navigate/message из замыкания первого рендера.
  const onCredential = (credential: string): void => {
    mutation.mutate(
      { id_token: credential },
      {
        onSuccess: (data) => {
          // Мультиконтекст: токены не выданы — на «Войти как…» (reuse
          // select-flow, как в LoginPage.onFinish). select_token — только
          // в location.state (в памяти), не в localStorage.
          if (isContextRequired(data)) {
            navigate('/select-context', {
              state: {
                select_token: data.select_token,
                contexts: data.contexts,
              } satisfies ContextPickerState,
            });
          }
          // TokenPair: setTokens уже сделан в onSuccess мутации → эффект
          // LoginPage (isAuthenticated) сам редиректит по роли.
        },
        onError: (err) => {
          if (err instanceof ApiError && err.status === 401) {
            void message.error(t('auth.login.errors.google_failed'));
          } else {
            void message.error(err instanceof Error ? err.message : t('auth.login.errors.generic'));
          }
        },
      },
    );
  };
  const onCredentialRef = useRef(onCredential);
  onCredentialRef.current = onCredential;

  useEffect(() => {
    if (!clientId) return undefined;
    let cancelled = false;

    const init = (): void => {
      const gsi = window.google?.accounts.id;
      const container = containerRef.current;
      if (cancelled || !gsi || !container) return;
      gsi.initialize({
        client_id: clientId,
        callback: (resp) => onCredentialRef.current(resp.credential),
      });
      // При смене локали эффект перезапускается — чистим контейнер, чтобы
      // renderButton не оставил рядом старую кнопку.
      container.replaceChildren();
      gsi.renderButton(container, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
        // NB (приёмка V1, проверено headless-прогоном): современный GIS
        // ИГНОРИРУЕТ locale и рендерит кнопку по Accept-Language браузера
        // (lt-браузер → литовская кнопка при locale:'ru'). Параметр оставлен
        // как заявление намерения + гигиена значений (только ru/kk).
        locale: (i18n.resolvedLanguage ?? i18n.language) === 'kk' ? 'kk' : 'ru',
      });
    };

    // Скрипт уже загружен (повторный визит на /login) — инициализируемся сразу.
    if (window.google?.accounts.id) {
      init();
      return () => {
        cancelled = true;
      };
    }

    // Ленивая загрузка GIS. Защита от двойной вставки (StrictMode/ремаунт):
    // ищем существующий <script> по src, вешаем свой onload-листенер на него.
    let script = document.querySelector<HTMLScriptElement>(`script[src="${GSI_SRC}"]`);
    if (!script) {
      script = document.createElement('script');
      script.src = GSI_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
    script.addEventListener('load', init);
    return () => {
      cancelled = true;
      script.removeEventListener('load', init);
      // Сам <script> не удаляем: GIS уже мог загрузиться, повторная вставка
      // при следующем визите бессмысленна — переиспользуем.
    };
  }, [clientId, i18n.language]);

  if (!clientId) return null;

  return (
    <>
      <Divider plain style={{ marginTop: 0, marginBottom: 16 }}>
        {t('auth.login.divider_or')}
      </Divider>
      <div
        ref={containerRef}
        data-testid="google-signin"
        style={{ display: 'flex', justifyContent: 'center', minHeight: 44 }}
      />
    </>
  );
}
